import { createGettext } from "vue3-gettext";
import {
  CREDENTIALS_ONLY_LANGUAGES,
  LANGUAGES,
  PROMOTER_LANGUAGES,
  PROMOTER_ONLY_LANGUAGES,
} from "@/constants";
import {
  ACCOUNT_IS_ACTIVE,
  ACCOUNT_TYPE,
  IS_PROMOTER_DOMAIN,
  PREVIOUS_ADMIN,
} from "@/windowConfig";
import AccountService from "@/services/account";

const AVAILABLE_LANGUAGES = [
  ...LANGUAGES,
  ...PROMOTER_ONLY_LANGUAGES,
  ...CREDENTIALS_ONLY_LANGUAGES,
].reduce((acc, { id, name }) => {
  acc[id] = name;
  return acc;
}, {});

const gettext = createGettext({
  availableLanguages: AVAILABLE_LANGUAGES,
  defaultLanguage: "en",
  silent: true,
});

const normalizeLanguage = language =>
  ["nb", "nb_NO", "no_NO"].includes(language) ? "no" : language;

export async function initializeLanguage() {
  let language = "en";

  try {
    const storedLanguage = localStorage.getItem("language") || "en";
    language = normalizeLanguage(storedLanguage);
    if (language !== storedLanguage) localStorage.setItem("language", language);
  } catch (error) {
    console.warn("Could not read the stored language", error);
  }

  if (ACCOUNT_IS_ACTIVE && PREVIOUS_ADMIN) {
    try {
      const { data } = await AccountService.getSessionInfo();
      const userLanguage = data.user?.language?.id;
      if (userLanguage) language = normalizeLanguage(userLanguage);
    } catch (error) {
      console.warn("Could not read the user's language", error);
    }
  }

  if (!ACCOUNT_IS_ACTIVE) {
    const queryLanguage = new URLSearchParams(window.location.search).get("lang");
    const credentialLanguages = [
      ...LANGUAGES,
      ...(IS_PROMOTER_DOMAIN ? PROMOTER_ONLY_LANGUAGES : []),
      ...CREDENTIALS_ONLY_LANGUAGES,
    ];
    const selectedLanguage = normalizeLanguage(queryLanguage);

    if (credentialLanguages.some(({ id }) => id === selectedLanguage)) {
      language = selectedLanguage;
      try {
        localStorage.setItem("language", language);
      } catch (error) {
        console.warn("Could not store the selected language", error);
      }
    }
  }

  const panelLanguages =
    ACCOUNT_TYPE === "promoter" ? PROMOTER_LANGUAGES : LANGUAGES;

  if (
    !Object.hasOwn(AVAILABLE_LANGUAGES, language) ||
    (ACCOUNT_IS_ACTIVE && !panelLanguages.some(({ id }) => id === language))
  ) {
    language = "en";
  }

  try {
    await loadLanguage(language);
  } catch (error) {
    console.error(`Could not load translations for ${language}`, error);

    if (language !== "en") {
      try {
        await loadLanguage("en");
      } catch (fallbackError) {
        console.error("Could not load English translations", fallbackError);
      }
    }
  }
}

async function loadLanguage(language) {
  const translationLanguage = language === "no" ? "no_NO" : language;
  const { default: translations } = await import(
    /* webpackChunkName: "language-[request]" */
    `@/language/compiled/${translationLanguage}.json`
  );
  gettext.translations = { [language]: translations[translationLanguage] };
  gettext.current = language;
}

export default gettext;
